import React from "react";

/**
 * Order status type matching the backend OrderStatus enum.
 * DRAFT is shown to self-service users as 'Oczekuje na opłacenie'.
 */
export type OrderStatus = "DRAFT" | "CONFIRMED" | "FULFILLED" | "CLOSED" | "CANCELED";

interface OrderStatusConfig {
  label: string;
  colorClasses: string;
}

/**
 * Badge color and label mapping for order statuses.
 *
 * Color semantics:
 * - blue = confirmed (pending fulfillment)
 * - green = fulfilled (completed successfully)
 * - gray/dark = closed (finalized, no further action)
 * - red = canceled (order was canceled)
 */
const ORDER_STATUS_CONFIG: Record<OrderStatus, OrderStatusConfig> = {
  DRAFT: {
    label: "Oczekuje na opłacenie",
    colorClasses: "bg-amber-100 text-amber-800",
  },
  CONFIRMED: {
    label: "Potwierdzone",
    colorClasses: "bg-blue-100 text-blue-800",
  },
  FULFILLED: {
    label: "Zrealizowane",
    colorClasses: "bg-green-100 text-green-800",
  },
  CLOSED: {
    label: "Zamknięte",
    colorClasses: "bg-gray-100 text-gray-800",
  },
  CANCELED: {
    label: "Anulowane",
    colorClasses: "bg-red-100 text-red-800",
  },
};

interface OrderStatusBadgeProps {
  /** Order status determining badge color and label */
  status: OrderStatus;
  /** Optional additional CSS classes */
  className?: string;
}

/**
 * Shared order status badge component for billing UI.
 *
 * Created by PA-7, imported by PA-8 (admin orders view).
 * Follows the same styling pattern as DocumentStatusBadge in BillingDocumentsList.tsx.
 */
export const OrderStatusBadge: React.FC<OrderStatusBadgeProps> = ({
  status,
  className = "",
}) => {
  const config = ORDER_STATUS_CONFIG[status] ?? { label: status, colorClasses: 'bg-gray-100 text-gray-800' };

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.colorClasses} ${className}`}
      aria-label={`Status zamowienia: ${config.label}`}
      data-testid="order-status-badge"
    >
      {config.label}
    </span>
  );
};
